import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ArrowLeft, ArrowRight, Download, Maximize2, Minimize2, ZoomIn, ZoomOut } from 'lucide-react';

export const ZOOM = { min: 0.5, max: 2, step: 0.1, start: 0.9 } as const;

type Props = {
  page: number;
  pages: number;
  zoom: number;
  url: string;
  isFullscreen: boolean;
  onPage: (page: number) => void;
  onZoom: (zoom: number) => void;
  onFullscreen: () => void;
};

/** The floating control pill: page, zoom, download and full screen. */
export function PdfToolbar({ page, pages, zoom, url, isFullscreen, onPage, onZoom, onFullscreen }: Props) {
  const round = (z: number) => Math.round(z * 20) / 20;
  return (
    <Stack direction="row" role="toolbar" aria-label="PDF controls" sx={pillSx}>
      <IconButton size="small" aria-label="Previous page" disabled={page <= 1} onClick={() => { onPage(page - 1); }}><ArrowLeft size={14} /></IconButton>
      <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center', fontWeight: 500 }} aria-live="polite">{page} / {pages || '–'}</Typography>
      <IconButton size="small" aria-label="Next page" disabled={pages === 0 || page >= pages} onClick={() => { onPage(page + 1); }}><ArrowRight size={14} /></IconButton>
      <Box aria-hidden sx={dividerSx} />
      <IconButton size="small" aria-label="Zoom out" disabled={zoom <= ZOOM.min} onClick={() => { onZoom(round(zoom - ZOOM.step)); }}><ZoomOut size={14} /></IconButton>
      <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center', fontWeight: 500 }}>{Math.round(zoom * 100)}%</Typography>
      <IconButton size="small" aria-label="Zoom in" disabled={zoom >= ZOOM.max} onClick={() => { onZoom(round(zoom + ZOOM.step)); }}><ZoomIn size={14} /></IconButton>
      <Box aria-hidden sx={dividerSx} />
      <Tooltip title="Download">
        <IconButton size="small" aria-label="Download PDF" component="a" href={url} download><Download size={14} /></IconButton>
      </Tooltip>
      <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'}>
        <IconButton size="small" aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'} onClick={onFullscreen}>
          {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

const pillSx = {
  alignItems: 'center',
  gap: 0.25,
  px: 1,
  py: 0.5,
  borderRadius: 5,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  boxShadow: '0 4px 16px rgba(26, 26, 25, 0.10)',
  color: 'text.primary',
  '& .MuiIconButton-root': { color: 'text.secondary', borderRadius: '50%', '&:hover': { color: 'text.primary' } },
} as const;

const dividerSx = { width: '1px', height: 16, bgcolor: 'divider', mx: 0.75 } as const;
